import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/time";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";

const POLL_MS = 60000;
const PREF_LABELS = [
  ["daily_digest", "Daily digest email"],
  ["weekly_digest", "Weekly digest email (Mondays)"],
  ["draft_ready", "Email when an AI draft is ready"],
  ["spike_alert", "Email when hostile comments spike"],
];

// In-app inbox plus this user's email preferences. Client users only: a super admin has no single client.
export default function NotificationBell({ isAdmin }) {
  const navigate = useNavigate();
  const [feed, setFeed] = useState({ items: [], unread: 0 });
  const [prefs, setPrefs] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => setFeed(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (open && isAdmin && !prefs) api.get("/notifications/prefs").then(({ data }) => setPrefs(data)).catch(() => {});
  }, [open, isAdmin, prefs]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };

  const readAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  const toggle = async (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    api.put("/notifications/prefs", { [key]: value }).then(({ data }) => setPrefs(data)).catch(() => {});
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell"
          aria-label={`Notifications${feed.unread ? `, ${feed.unread} unread` : ""}`}
          className="relative shrink-0 p-2 rounded-md text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent/[0.06] transition-colors"
        >
          <Bell className="w-4 h-4" strokeWidth={1.5} />
          {feed.unread > 0 && (
            <span data-testid="notification-count" className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-sidebar-primary text-[9px] font-semibold leading-[14px] text-black text-center">
              {feed.unread > 9 ? "9+" : feed.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="text-sm font-medium">Notifications</p>
          {feed.unread > 0 && (
            <button type="button" onClick={readAll} className="text-xs text-muted-foreground hover:text-foreground">Mark all read</button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto">
          {feed.items.length === 0 && <p className="px-4 py-6 text-sm text-muted-foreground">Nothing yet.</p>}
          {feed.items.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => openItem(n)}
              className="w-full text-left px-4 py-3 border-b border-border/50 hover:bg-accent/40 transition-colors"
            >
              <p className={`text-[13px] ${n.read ? "text-muted-foreground" : "font-medium"}`}>{n.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
              <p className="text-[11px] text-muted-foreground/70 mt-1">{timeAgo(n.created_at)}</p>
            </button>
          ))}
        </div>
        {isAdmin && prefs && (
          <div className="px-4 py-3 space-y-2.5" data-testid="notification-prefs">
            {PREF_LABELS.map(([key, label]) => (
              <label key={key} className="flex items-center justify-between gap-3 text-xs">
                <span>{label}</span>
                <Switch checked={!!prefs[key]} onCheckedChange={(v) => toggle(key, v)} aria-label={label} />
              </label>
            ))}
            {!prefs.email_live && <p className="text-[11px] text-muted-foreground">Email sending is not switched on yet, so these are saved but nothing is sent.</p>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
